import { useCallback, useMemo } from 'react';
import type { RuleStep, RuleTrigger, RulesCatalog } from '@/types/rules.types';
import { flattenSteps } from '../lib/rule-tree';
import { describeStep } from '../lib/rule-summary';
import { useTicketRules } from './use-ticket-rules';

const upperFirst = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Names a traced step in plain words ("If the message is small talk is Greeting") from the brand's
 * saved playbooks, plus an unsaved one the builder is editing.
 */
export function useStepLabels(catalog: RulesCatalog | undefined, draft?: { id: string; steps: RuleStep[]; trigger?: RuleTrigger }) {
	const { data: rules } = useTicketRules();
	const labels = useMemo(() => {
		const map = new Map<string, string>();
		if (!catalog) return map;
		const add = (ruleId: string, steps: RuleStep[], trigger?: RuleTrigger) => {
			for (const step of flattenSteps(steps)) map.set(`${ruleId}:${step.id}`, upperFirst(describeStep(step, catalog, trigger)));
		};
		for (const rule of rules ?? []) add(rule.id, rule.steps, rule.trigger);
		if (draft) add(draft.id, draft.steps, draft.trigger);
		return map;
	}, [catalog, rules, draft]);
	return useCallback((ruleId: string, stepId: string) => labels.get(`${ruleId}:${stepId}`), [labels]);
}
